import React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardReturnIcon from '@mui/icons-material/KeyboardReturn';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Typography } from '@mui/material';

import { CHECKLIST_STATUS_STYLES } from '../../constants';
import {
  getEffectiveChecklistStatus,
  isRowApproved,
  isRowManuallyApproved,
  isRowManuallyRequired,
} from '../../manualOverridePersistence';
import ChecklistStatusDot from '../../reportCards/ChecklistStatusDot';
import EmptyTableRow from '../../reportCards/EmptyTableRow';
import HiddenApprovedFooter from '../../reportCards/HiddenApprovedFooter';
import ReadOnlyField from '../../reportCards/ReadOnlyField';
import ReportCard from '../../reportCards/ReportCard';
import ReportNote from '../../reportCards/ReportNote';
import SortTableCell from '../../reportCards/SortTableCell';
import { type MarginBand, formatMoney } from '../../shared/checklistUtils';
import type { ManualOverrideState } from '../../types';

import type { EgsMarginChecklistRow } from './types';

interface Props {
  hiddenApprovedCount: number;
  manualOverrides: ManualOverrideState;
  rows: Array<EgsMarginChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onUnapprovalChange: (rowId: string) => void;
}

export default function MarginReportCard({
  hiddenApprovedCount,
  manualOverrides,
  rows,
  showAutoApproved,
  onApprovalChange,
  onUnapprovalChange,
}: Props) {
  return (
    <ReportCard>
      <ReportNote
        icon={<InfoOutlinedIcon />}
        text={
          <>
            <strong>Extra Guest Surcharges margin check:</strong> red is <strong>10% or below</strong>, yellow is{' '}
            <strong>above 10% up to 18%</strong>, and green is <strong>19%+</strong>.
          </>
        }
      />
      <TableContainer>
        <Table size="small" sx={{ minWidth: 1860 }}>
          <TableHead>
            <TableRow>
              <SortTableCell>Room</SortTableCell>
              <SortTableCell>Extra Guest Type</SortTableCell>
              <SortTableCell>Age Range</SortTableCell>
              <SortTableCell>Cost Price</SortTableCell>
              <SortTableCell>Sell Price</SortTableCell>
              <SortTableCell>% Margin</SortTableCell>
              <SortTableCell>Margin Check</SortTableCell>
              <SortTableCell>Bedding Type</SortTableCell>
              <SortTableCell>Inclusions</SortTableCell>
              <SortTableCell>Collected By</SortTableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={11} label="No EGS rows match this view." />
            ) : (
              rows.map((row) => {
                const manuallyApproved = isRowManuallyApproved(row, manualOverrides);
                const manuallyRequired = isRowManuallyRequired(row, manualOverrides);
                const approved = isRowApproved(row, manualOverrides);

                return (
                  <TableRow key={row.id} hover sx={approved ? CHECKLIST_STATUS_STYLES.approvedRow : undefined}>
                    <TableCell sx={{ minWidth: 360 }}>
                      <Typography variant="body2" fontWeight={500}>
                        {row.name}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 170 }}>
                      <ReadOnlyField value={row.extraGuestType || 'N/A'} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>
                      <ReadOnlyField value={row.ageRange || 'N/A'} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <ReadOnlyField value={formatMoney(row.costPrice, row.currencyCode)} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <ReadOnlyField value={formatMoney(row.sellPrice, row.currencyCode)} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <ReadOnlyField value={typeof row.margin === 'number' ? `${row.margin.toFixed(1)}%` : 'N/A'} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 170 }}>
                      <ChecklistStatusDot
                        approvalType={manuallyApproved ? 'manual' : 'auto'}
                        status={getEffectiveChecklistStatus(row, manualOverrides)}
                        band={getMarginStatusBand({ approved, manuallyRequired, marginBand: row.marginBand })}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 380 }}>
                      <ReadOnlyField value={row.beddingType || 'N/A'} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 280 }}>
                      <ReadOnlyField value={row.inclusions || 'N/A'} />
                    </TableCell>
                    <TableCell sx={{ minWidth: 210 }}>
                      <ReadOnlyField value={row.collectedBy || 'N/A'} />
                    </TableCell>
                    <TableCell>
                      {approved ? (
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<KeyboardReturnIcon />}
                          onClick={() => onUnapprovalChange(row.id)}
                        >
                          Unapprove
                        </Button>
                      ) : (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => onApprovalChange(row.id)}
                        >
                          Approve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="EGS row" showAutoApproved={showAutoApproved} />
    </ReportCard>
  );
}

function getMarginStatusBand({
  approved,
  manuallyRequired,
  marginBand,
}: {
  approved: boolean;
  manuallyRequired: boolean;
  marginBand?: MarginBand;
}) {
  if (approved) {
    return 'green';
  }

  if (manuallyRequired) {
    return undefined;
  }

  return marginBand;
}
